import React from 'react';
import {
  Checkbox,
  DateInput,
  Input,
  Select,
  SelectContent,
  SelectTrigger,
  Text,
  Textarea,
} from '@optiaxiom/react';

import { Filter, FilterBuilder } from '@frontend/components/common/FilterBuilder';

import { INodeProperties, NodeParameterValueType } from '../../types/node-properties';

import { ParameterManager } from './ParameterManager';
import { styles } from './ParameterRenderer.css';

type PropertyRendererProps = {
  property: INodeProperties;
  value: NodeParameterValueType;
  onChange: (value: NodeParameterValueType) => void;
  isVisible?: boolean;
  parameters?: Record<string, NodeParameterValueType>;
  error?: string;
};

export const PropertyRenderer: React.FC<PropertyRendererProps> = ({
  property,
  value,
  onChange,
  isVisible = true,
  error,
}) => {
  if (!isVisible) {
    return null;
  }

  const parameterManager = new ParameterManager();
  const isReadonly = parameterManager.isParameterReadonly(property);

  const handleChange = (newValue: NodeParameterValueType) => {
    if (!isReadonly) {
      onChange(newValue);
    }
  };

  const renderInput = () => {
    switch (property.type) {
      case 'string':
        if (property.typeOptions?.rows && property.typeOptions.rows > 1) {
          return (
            <Textarea
              value={String(value || '')}
              onChange={e => handleChange(e.target.value)}
              placeholder={property.placeholder}
              resize="vertical"
              readOnly={isReadonly}
            />
          );
        }
        return (
          <Input
            type={property.typeOptions?.password ? 'password' : 'text'}
            value={String(value || '')}
            onChange={e => handleChange(e.target.value)}
            placeholder={property.placeholder}
            readOnly={isReadonly}
          />
        );

      case 'number':
        return (
          <Input
            type="number"
            value={String(value || '')}
            onChange={e => handleChange(Number(e.target.value))}
            min={property.typeOptions?.minValue}
            max={property.typeOptions?.maxValue}
            placeholder={property.placeholder}
            readOnly={isReadonly}
          />
        );

      case 'boolean':
        return (
          <Checkbox
            checked={Boolean(value)}
            onChange={checked => handleChange(Boolean(checked))}
            readOnly={isReadonly}
          />
        );

      case 'options':
        if (!property.options) {
          return <Text>No options available</Text>;
        }
        return (
          <Select
            value={String(value || '')}
            onValueChange={(newValue: string) => handleChange(newValue)}
            options={property.options.map(
              (option: { name: string; value: string | number | boolean }) => ({
                label: option.name,
                value: String(option.value),
              })
            )}
            disabled={isReadonly}
          >
            <SelectTrigger />
            <SelectContent />
          </Select>
        );

      case 'json':
        return (
          <Textarea
            value={typeof value === 'string' ? value : JSON.stringify(value, null, 2)}
            onChange={e => {
              try {
                const parsed = JSON.parse(e.target.value);
                handleChange(parsed);
              } catch {
                // Keep the string value for now, validation will catch it
                handleChange(e.target.value);
              }
            }}
            placeholder={property.placeholder || 'Enter JSON...'}
            resize="vertical"
            readOnly={isReadonly}
          />
        );

      case 'dateTime':
        return (
          <DateInput
            type="datetime-local"
            value={value ? String(value) : ''}
            onValueChange={newValue => {
              // DateInput returns ISO string format which the backend expects
              handleChange(newValue || null);
            }}
            readOnly={isReadonly}
          />
        );

      case 'filters':
        return (
          <FilterBuilder
            value={(value as Filter[]) || []}
            onChange={(newFilters: Filter[]) => handleChange(newFilters)}
            label={property.displayName}
            description={property.description}
            error={error}
          />
        );

      default:
        return (
          <Input
            value={String(value || '')}
            onChange={e => handleChange(e.target.value)}
            placeholder={property.placeholder}
            readOnly={isReadonly}
          />
        );
    }
  };

  // For filters type, render without the wrapper since it has its own layout
  if (property.type === 'filters') {
    return <div className={styles.fieldGroup}>{renderInput()}</div>;
  }

  return (
    <div className={styles.fieldGroup}>
      <div className={styles.labelRow}>
        <span className={styles.fieldLabel}>{property.displayName}</span>
        {property.required && <span className={styles.requiredIndicator}>*</span>}
      </div>

      <div className={styles.inputContainer}>{renderInput()}</div>

      {property.hint && <div className={styles.fieldHint}>{property.hint}</div>}
    </div>
  );
};
